import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  XMarkIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { CalendarDays, FileText, Settings, Banknote, CheckCircle, XCircle } from "lucide-react";
import api from "@/services/api";
import toast from "react-hot-toast";

interface LeaveType {
  id: number;
  name: string;
  code: string;
  maxDaysPerYear: number | null;
  carryForwardAllowed: boolean;
  maxCarryForwardDays: number;
  requiresApproval: boolean;
  isPaid: boolean;
  description: string | null;
  isActive: boolean;
  _count: {
    employeeLeaveBalances: number;
    leaveRequests: number;
  };
}

interface LeaveTypesResponse {
  leaveTypes: LeaveType[];
}

interface LeaveTypeFormData {
  name: string;
  code: string;
  maxDaysPerYear: string;
  carryForwardAllowed: boolean;
  maxCarryForwardDays: string;
  requiresApproval: boolean;
  isPaid: boolean;
  description: string;
}

const LeaveTypes: React.FC = () => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingLeaveType, setEditingLeaveType] = useState<LeaveType | null>(null);
  const [formData, setFormData] = useState<LeaveTypeFormData>({
    name: "",
    code: "",
    maxDaysPerYear: "",
    carryForwardAllowed: false,
    maxCarryForwardDays: "0",
    requiresApproval: true,
    isPaid: true,
    description: "",
  });

  const queryClient = useQueryClient();

  // Fetch Leave Types
  const { data: leaveTypesData, isLoading } = useQuery<LeaveTypesResponse>({
    queryKey: ["leaveTypes"],
    queryFn: async () => {
      const response = await api.get("/leaves/types");
      return response.data.data;
    },
  });

  // Create Leave Type
  const createMutation = useMutation({
    mutationFn: async (data: LeaveTypeFormData) => {
      const payload = {
        ...data,
        maxDaysPerYear: data.maxDaysPerYear ? parseInt(data.maxDaysPerYear) : null,
        maxCarryForwardDays: parseInt(data.maxCarryForwardDays),
      };
      const response = await api.post("/leaves/types", payload);
      return response.data;
    },
    onSuccess: () => {
      toast.success("Leave type created successfully");
      queryClient.invalidateQueries({ queryKey: ["leaveTypes"] });
      resetForm();
      setShowAddForm(false);
    },
    onError: (error: any) =>
      toast.error(error.response?.data?.error?.message || "Failed to create leave type"),
  });

  // Update Leave Type
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<LeaveTypeFormData> }) => {
      const payload = {
        ...data,
        maxDaysPerYear: data.maxDaysPerYear ? parseInt(data.maxDaysPerYear) : null,
        maxCarryForwardDays: data.maxCarryForwardDays
          ? parseInt(data.maxCarryForwardDays)
          : undefined,
      };
      const response = await api.put(`/leaves/types/${id}`, payload);
      return response.data;
    },
    onSuccess: () => {
      toast.success("Leave type updated successfully");
      queryClient.invalidateQueries({ queryKey: ["leaveTypes"] });
      setEditingLeaveType(null);
      resetForm();
    },
    onError: (error: any) =>
      toast.error(error.response?.data?.error?.message || "Failed to update leave type"),
  });

  // Delete Leave Type
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await api.delete(`/leaves/types/${id}`);
      return response.data;
    },
    onSuccess: () => {
      toast.success("Leave type deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["leaveTypes"] });
    },
    onError: (error: any) =>
      toast.error(error.response?.data?.error?.message || "Failed to delete leave type"),
  });

  const resetForm = () => {
    setFormData({
      name: "",
      code: "",
      maxDaysPerYear: "",
      carryForwardAllowed: false,
      maxCarryForwardDays: "0",
      requiresApproval: true,
      isPaid: true,
      description: "",
    });
  };

  const handleEdit = (lt: LeaveType) => {
    setFormData({
      name: lt.name,
      code: lt.code,
      maxDaysPerYear: lt.maxDaysPerYear?.toString() || "",
      carryForwardAllowed: lt.carryForwardAllowed,
      maxCarryForwardDays: lt.maxCarryForwardDays.toString(),
      requiresApproval: lt.requiresApproval,
      isPaid: lt.isPaid,
      description: lt.description || "",
    });
    setEditingLeaveType(lt);
    setShowAddForm(true);
  };

  const handleDelete = async (lt: LeaveType) => {
    if (!confirm(`Are you sure you want to delete "${lt.name}"?`)) return;
    deleteMutation.mutate(lt.id);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingLeaveType) {
      updateMutation.mutate({ id: editingLeaveType.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const getStatusBadge = (isActive: boolean) => (
    <span
      className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${
        isActive
          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
          : "bg-red-50 text-red-700 border-red-200"
      }`}
    >
      {isActive ? (
        <>
          <CheckCircle className="w-3 h-3 mr-1" /> Active
        </>
      ) : (
        <>
          <XCircle className="w-3 h-3 mr-1" /> Inactive
        </>
      )}
    </span>
  );

  const totalLeaveTypes = leaveTypesData?.leaveTypes?.length || 0;
  const totalEmployees = leaveTypesData?.leaveTypes?.reduce((acc, lt) => acc + lt._count.employeeLeaveBalances, 0) || 0;
  const totalRequests = leaveTypesData?.leaveTypes?.reduce((acc, lt) => acc + lt._count.leaveRequests, 0) || 0;

  return (
    <div className="max-w-7xl mx-auto space-y-5">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3 mb-4 lg:mb-0">
          <div className="h-10 w-10 flex items-center justify-center rounded-lg bg-indigo-50 dark:bg-indigo-900/20">
            <Settings className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Leave Types</h1>
            <p className="text-gray-500 dark:text-gray-400 text-xs">
              Manage and configure leave type definitions for your organization
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {totalLeaveTypes} types / {totalEmployees} employees / {totalRequests} requests
          </div>
          <button
            onClick={() => {
              setShowAddForm(!showAddForm);
              if (!showAddForm) {
                resetForm();
                setEditingLeaveType(null);
              }
            }}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium transition-all duration-200 hover:shadow-md text-xs"
          >
            <PlusIcon className="h-3 w-3" />
            {showAddForm ? "Close Form" : "Add Leave Type"}
          </button>
        </div>
      </div>

      {/* Enhanced Form */}
      {showAddForm && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card p-4">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                {editingLeaveType ? "Edit Leave Type" : "Create New Leave Type"}
              </h4>
              <p className="text-gray-600 dark:text-gray-300 text-xs mt-1">
                Configure the settings for this leave type
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setShowAddForm(false);
                setEditingLeaveType(null);
                resetForm();
              }}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Basic Information Section */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
              <h5 className="text-xs font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <FileText size={14} className="text-indigo-600 dark:text-indigo-400" />
                Basic Information
              </h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Leave Type Name *
                  </label>
                  <input
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-indigo-600 focus:border-transparent bg-white dark:bg-gray-600 text-gray-900 dark:text-white text-xs transition-all duration-200"
                    placeholder="e.g., Annual Leave"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Code *
                  </label>
                  <input
                    required
                    value={formData.code}
                    onChange={(e) =>
                      setFormData({ ...formData, code: e.target.value.toUpperCase() })
                    }
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-indigo-600 focus:border-transparent bg-white dark:bg-gray-600 text-gray-900 dark:text-white text-xs uppercase transition-all duration-200"
                    placeholder="e.g., ANNUAL"
                  />
                </div>
              </div>
            </div>

            {/* Duration & Limits Section */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
              <h5 className="text-xs font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <CalendarDays size={14} className="text-indigo-600 dark:text-indigo-400" />
                Duration & Limits
              </h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Maximum Days Per Year
                    <span className="text-gray-400 text-xs ml-1">(Optional)</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.maxDaysPerYear}
                    onChange={(e) =>
                      setFormData({ ...formData, maxDaysPerYear: e.target.value })
                    }
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-indigo-600 focus:border-transparent bg-white dark:bg-gray-600 text-gray-900 dark:text-white text-xs transition-all duration-200"
                    placeholder="Leave empty for unlimited"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Leave empty for unlimited days
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Maximum Carry Forward Days
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.maxCarryForwardDays}
                    onChange={(e) =>
                      setFormData({ ...formData, maxCarryForwardDays: e.target.value })
                    }
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-indigo-600 focus:border-transparent bg-white dark:bg-gray-600 text-gray-900 dark:text-white text-xs transition-all duration-200"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Maximum days that can be carried forward to next year
                  </p>
                </div>
              </div>
            </div>

            {/* Settings Section */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
              <h5 className="text-xs font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <Settings size={14} className="text-indigo-600 dark:text-indigo-400" />
                Leave Settings
              </h5>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="flex items-start gap-2 p-3 bg-white dark:bg-gray-600 rounded-lg border border-gray-200 dark:border-gray-500 hover:border-gray-300 dark:hover:border-gray-400 transition-all duration-200">
                  <input
                    type="checkbox"
                    checked={formData.carryForwardAllowed}
                    onChange={(e) =>
                      setFormData({ ...formData, carryForwardAllowed: e.target.checked })
                    }
                    className="h-3 w-3 text-indigo-600 border-gray-300 rounded mt-0.5 focus:ring-indigo-600"
                  />
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                      Allow Carry Forward
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Unused days can be carried to next year
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-2 p-3 bg-white dark:bg-gray-600 rounded-lg border border-gray-200 dark:border-gray-500 hover:border-gray-300 dark:hover:border-gray-400 transition-all duration-200">
                  <input
                    type="checkbox"
                    checked={formData.requiresApproval}
                    onChange={(e) =>
                      setFormData({ ...formData, requiresApproval: e.target.checked })
                    }
                    className="h-3 w-3 text-indigo-600 border-gray-300 rounded mt-0.5 focus:ring-indigo-600"
                  />
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                      Requires Approval
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Leave requests need manager approval
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-2 p-3 bg-white dark:bg-gray-600 rounded-lg border border-gray-200 dark:border-gray-500 hover:border-gray-300 dark:hover:border-gray-400 transition-all duration-200">
                  <input
                    type="checkbox"
                    checked={formData.isPaid}
                    onChange={(e) =>
                      setFormData({ ...formData, isPaid: e.target.checked })
                    }
                    className="h-3 w-3 text-indigo-600 border-gray-300 rounded mt-0.5 focus:ring-indigo-600"
                  />
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                      Paid Leave
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Employee is paid during this leave
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Description Section */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
              <h5 className="text-xs font-semibold text-gray-900 dark:text-white mb-3">
                Description
              </h5>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Leave Type Description
                  <span className="text-gray-400 text-xs ml-1">(Optional)</span>
                </label>
                <textarea
                  rows={3}
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-indigo-600 focus:border-transparent bg-white dark:bg-gray-600 text-gray-900 dark:text-white text-xs resize-none transition-all duration-200"
                  placeholder="Describe the purpose and rules for this leave type..."
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  This description will help employees understand when to use this leave type
                </p>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-600">
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  resetForm();
                }}
                className="px-3 py-2 text-xs font-medium bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 dark:bg-gray-600 dark:border-gray-500 dark:text-gray-300 dark:hover:bg-gray-500 transition-all duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
                className="inline-flex items-center gap-2 px-3 py-2 text-xs font-medium bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {createMutation.isPending || updateMutation.isPending ? (
                  <>
                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {editingLeaveType ? "Updating..." : "Creating..."}
                  </>
                ) : editingLeaveType ? (
                  "Update Leave Type"
                ) : (
                  "Create Leave Type"
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
            Existing Leave Types
          </h2>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {totalLeaveTypes} types / {totalEmployees} employees / {totalRequests} requests
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="h-8 w-8 rounded-full border-[3px] border-indigo-600 border-t-transparent animate-spin"></div>
          </div>
        ) : !leaveTypesData?.leaveTypes?.length ? (
          <div className="text-center py-8">
            <div className="h-12 w-12 mx-auto bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center mb-3">
              <CalendarDays className="text-gray-400 h-5 w-5" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">No Leave Types Found</h3>
            <p className="text-gray-600 dark:text-gray-300 text-xs mb-4">
              Get started by creating your first leave type
            </p>
            <button
              onClick={() => setShowAddForm(true)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium transition-all duration-200 hover:shadow-md text-xs"
            >
              <PlusIcon className="h-3 w-3" /> Create First Leave Type
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table min-w-full text-xs">
              <thead>
                <tr>
                  {[
                    "Name",
                    "Code",
                    "Max Days",
                    "Paid",
                    "Approval",
                    "Status",
                    "Employees",
                    "Requests",
                    "Actions",
                  ].map((h) => (
                    <th key={h} className="px-4 py-2 text-left font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {leaveTypesData.leaveTypes.map((lt) => (
                  <tr
                    key={lt.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-200"
                  >
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-white text-xs">{lt.name}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300 font-medium text-xs">{lt.code}</td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300 text-xs">{lt.maxDaysPerYear ?? "Unlimited"}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${
                          lt.isPaid
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : "bg-red-50 text-red-700 border-red-200"
                        }`}
                      >
                        {lt.isPaid ? (
                          <>
                            <Banknote className="w-3 h-3 mr-1" /> Paid
                          </>
                        ) : (
                          <>
                            <XCircle className="w-3 h-3 mr-1" /> Unpaid
                          </>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300 font-medium text-xs">
                      {lt.requiresApproval ? "Required" : "Auto"}
                    </td>
                    <td className="px-4 py-3">{getStatusBadge(lt.isActive)}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300 text-center font-medium text-xs">
                      {lt._count.employeeLeaveBalances}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300 text-center font-medium text-xs">
                      {lt._count.leaveRequests}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => handleEdit(lt)}
                          className="text-gray-400 hover:text-indigo-600 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
                          title="Edit leave type"
                        >
                          <PencilIcon className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => handleDelete(lt)}
                          className="text-gray-400 hover:text-red-600 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
                          title="Delete leave type"
                        >
                          <TrashIcon className="h-3 w-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaveTypes;